import React, { useContext, useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import IconButton from 'components/common/IconButton';
import { Link, useParams } from 'react-router-dom';
import CartItem from './CartItem';
import CartModal from './CartModal';
import { ProductContext } from 'context/Context';
import { getSubtotal } from 'helpers/utils';
import { useTranslation, Trans } from 'react-i18next';
import useProduct from 'y4/hooks/useProduct';

const ShoppingCart = () => {
  const { t } = useTranslation('commerce');
  const params = useParams();

  const { loadPromo } = useProduct();

  const [totalCost, setTotalCost] = useState(0);
  const [promoCode, setPromoCode] = useState('');

  const {
    productsState: { config, cartItems },
    productsDispatch
  } = useContext(ProductContext);
  const currency = config.currency;

  useEffect(() => {
    setTotalCost(getSubtotal(cartItems));
  }, [cartItems]);

  const applyPromo = e => {
    e.preventDefault();
    loadPromo(promoCode).then(rt => {
      productsDispatch({
        type: 'APPLY_PROMO',
        payload: {
          rt
        }
      });
      if (rt.isOk()) setPromoCode('');
    });
  };

  return (
    <>
      <Card>
        <Card.Header>
          <Row className="justify-content-between">
            <Col md="auto">
              <h5 className="mb-3 mb-md-0">
                <Trans ns="commerce">Shopping Cart</Trans> {cartItems.length}{' '}
                <Trans ns="commerce">items</Trans>
              </h5>
            </Col>
            <Col md="auto" className="d-flex justify-content-end">
              <IconButton
                className="border-300 me-2"
                iconClassName="me-1"
                variant="outline-secondary"
                size="sm"
                icon="chevron-left"
                transform="shrink-4"
                as={Link}
                to={`/${params.corp}/e-commerce/product/product-grid`}
              >
                <Trans ns="commerce">Continue Shopping</Trans>
              </IconButton>
              {cartItems.length > 0 ? (
                <Button
                  as={Link}
                  to={`/${params.corp}/e-commerce/checkout`}
                  variant="primary"
                  size="sm"
                >
                  <Trans ns="commerce">Checkout</Trans>
                </Button>
              ) : null}
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="p-0">
          {cartItems.length > 0 ? (
            <>
              <Row className="gx-card mx-0 bg-200 text-900 fs--1 fw-semi-bold">
                <Col xs={9} md={8} className="py-2">
                  <Trans ns="commerce">Name</Trans>
                </Col>
                <Col xs={3} md={4}>
                  <Row>
                    <Col md={8} className="py-2 d-none d-md-block text-center">
                      <Trans ns="commerce">Quantity</Trans>
                    </Col>
                    <Col xs={12} md={4} className="text-end py-2">
                      <Trans ns="commerce">Price</Trans>
                    </Col>
                  </Row>
                </Col>
              </Row>
              {cartItems.map(product => (
                <CartItem key={product.id} product={product} />
              ))}
              <Row className="fw-bold gx-card mx-0">
                <Col xs={9} md={8} className="py-2 text-end text-900">
                  <Trans ns="commerce">Total</Trans>
                </Col>
                <Col className="px-0">
                  <Row className="gx-card mx-0">
                    <Col md={7} className="py-2 d-none d-md-block text-center">
                      {cartItems.length} {t('items')}
                    </Col>
                    <Col
                      xs={12}
                      md={5}
                      className="text-end py-2 text-nowrap px-x1"
                    >
                      {currency}
                      {totalCost}
                    </Col>
                  </Row>
                </Col>
              </Row>
            </>
          ) : (
            <p className="p-x1 mb-0 bg-light">
              <Trans ns="commerce">
                You have no items in your shopping cart. Go ahead and start
                shopping!
              </Trans>
            </p>
          )}
        </Card.Body>

        {cartItems.length > 0 && (
          <Card.Footer className="bg-light d-flex justify-content-end">
            <Form
              className="me-3"
              style={{ width: '250px' }}
              onSubmit={applyPromo}
            >
              <div className="input-group input-group-sm">
                <Form.Control
                  type="text"
                  placeholder={config.promoPlaceholder}
                  value={promoCode}
                  onChange={e => setPromoCode(e.target.value)}
                />
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="border-300"
                  type="submit"
                >
                  <Trans ns="commerce">Apply</Trans>
                </Button>
              </div>
            </Form>
            <div className="flex-grow-1"></div>
            <Button
              className="text-nowrap"
              as={Link}
              to={`/${params.corp}/e-commerce/checkout`}
              variant="primary"
              size="sm"
            >
              <Trans ns="commerce">Checkout</Trans>
            </Button>
          </Card.Footer>
        )}
      </Card>
      <CartModal />
    </>
  );
};

export default ShoppingCart;
